<template>
  <div class="notice-wrapper">
    <template v-if="additionalConsentUrlList.length > 0 && !loading">
      <div
        class="notice"
        v-for="(consentUrl, index) in additionalConsentUrlList"
        :key="`consent-url-${index}`"
      >
        <div class="notice__warning">
          <b-icon icon="exclamation-triangle" variant="warning" font-scale="3" />
        </div>
        <div class="notice__content">
          <p class="notice__title tw-mb-1.5">
            {{ $t("gateways.crowded_additional_consent_url_title", "Additional Consent Required") }}
          </p>
          <p class="notice__text tw-mb-1.5">
            {{
              $t(
                "gateways.crowded_additional_consent_url_text",
                "To comply with regulatory requirements, Crowded requires additional consent from you. Please review and provide your consent by clicking the button below."
              )
            }}
          </p>
        </div>
        <button
          class="bttn-bordered bttn-bordered--sm"
          @click="openAdditionalConsentUrlModal(consentUrl)"
        >
          {{ $t("gateways.crowded_additional_consent_url_button", "Provide Additional Consent") }}
        </button>
      </div>
    </template>

    <template v-if="bankApplicationFormNotices.length > 0 && !loading">
      <div
        class="notice"
        v-for="(notice, index) in bankApplicationFormNotices"
        :key="`form-url-${index}`"
      >
        <div class="notice__warning">
          <b-icon icon="exclamation-triangle" variant="warning" font-scale="3" />
        </div>
        <div class="notice__content">
          <p class="notice__title tw-mb-1.5">
            {{ formNoticeTitle(notice.status) }}
          </p>
          <p class="notice__text tw-mb-1.5">
            {{ formNoticeText(notice.status) }}
          </p>
        </div>
        <button
          v-if="notice.url && formNoticeHasAction(notice.status)"
          class="bttn-bordered bttn-bordered--sm"
          @click="openBankApplicationFormModal(notice.url, formNoticeTitle(notice.status))"
        >
          {{ formNoticeButton(notice.status) }}
        </button>
      </div>
    </template>

    <template v-if="payoutSetupNeeded && !loading">
      <div class="notice">
        <div class="notice__warning">
          <b-icon icon="exclamation-triangle" variant="warning" font-scale="3" />
        </div>
        <div class="notice__content">
          <p class="notice__title tw-mb-1.5">
            {{ $t("gateways.crowded_payout_prompt_title", "Enable automatic payouts") }}
          </p>
          <p class="notice__text tw-mb-1.5">
            {{
              $t(
                "gateways.crowded_payout_prompt_text",
                "Your Crowded account is approved. Add your bank account so released donation funds are automatically deposited to you."
              )
            }}
          </p>
        </div>
        <button class="bttn-bordered bttn-bordered--sm" @click="showPayoutModal = true">
          {{ $t("gateways.crowded_payout_prompt_button", "Add bank account") }}
        </button>
      </div>
    </template>

    <AdditionalConsentUrlModal ref="additionalConsentUrlModal" />

    <b-modal
      v-model="showPayoutModal"
      size="lg"
      centered
      hide-footer
      :title="$t('gateways.crowded_payout_title', 'Automatic payouts')"
    >
      <crowded-payout-account
        v-if="payoutSetupGatewayId"
        :gateway-id="payoutSetupGatewayId"
        :approved="true"
        :show-title="false"
        @updated="onPayoutUpdated"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import AdditionalConsentUrlModal from "./additional-consent-url-modal.vue";
import CrowdedPayoutAccount from "@/views/gateways/subcomponents/crowdedPayoutAccount.vue";

export default {
  name: "CrowdedConsentUrl",
  components: {
    AdditionalConsentUrlModal,
    CrowdedPayoutAccount,
  },
  data() {
    return {
      abortController: null,
      loading: false,
      gatewayDataList: [],
      showPayoutModal: false,
    };
  },
  computed: {
    additionalConsentUrlList() {
      return this.gatewayDataList
        .map((gtwData) =>
          gtwData.bank_application_status !== "Approved"
            ? gtwData.additional_consent_url || null
            : null
        )
        .filter((url) => url !== null);
    },
    bankApplicationFormNotices() {
      return this.gatewayDataList
        .filter((gtwData) => gtwData.bank_application_status !== "Approved")
        .map((gtwData) => ({
          status: gtwData.form_status || "",
          url: gtwData.form_url || "",
        }))
        .filter((notice) => notice.status || notice.url);
    },
    // The first approved Crowded gateway with no payout counterparty saved
    // yet, or null.
    payoutSetupGatewayId() {
      const gtwData = this.gatewayDataList.find(
        (d) =>
          d.gatewayId &&
          d.bank_application_status === "Approved" &&
          d.payout_ach_status === "missing"
      );
      return gtwData ? gtwData.gatewayId : null;
    },
    payoutSetupNeeded() {
      return this.payoutSetupGatewayId !== null;
    },
  },
  mounted() {
    this.abortController = new AbortController();
    this.loading = true;
    const params = { gateway: "crowded" };
    this.getGatewaysAndReturn({ signal: this.abortController.signal, params })
      .then(async (gateways) => {
        this.gatewayDataList = this.extractGtwData(gateways);
      })
      .finally(() => {
        this.loading = false;
      });
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    ...mapActions({
      getGatewaysAndReturn: "getGatewaysAndReturn",
    }),

    // gatewayId is set only on base "crowded" rows — the ones payout requests
    // can name.
    extractGtwData(gateways) {
      return gateways.map((gtw) => ({
        ...(gtw.attributes.gateway_data || {}),
        gatewayId: gtw.attributes.gateway === "crowded" ? gtw.id : null,
      }));
    },

    openAdditionalConsentUrlModal(url) {
      this.$refs.additionalConsentUrlModal.open(url);
    },

    openBankApplicationFormModal(url, title) {
      this.$refs.additionalConsentUrlModal.open(
        url,
        title ||
          this.$t("gateways.crowded_bank_application_form_title", "Complete Bank Application")
      );
    },

    formNoticeTitle(status) {
      switch (status) {
        case "EnterBusinessInformation":
          return this.$t("gateways.crowded_form_resume_title", "Resume your application");
        case "InProgressByAnotherUser":
          return this.$t("gateways.crowded_form_in_progress_title", "Application in progress");
        case "ApplicationExists":
          return this.$t("gateways.crowded_form_submitted_title", "Application submitted");
        case "PendingCreation":
          return this.$t("gateways.crowded_form_pending_title", "Application in progress");
        default:
          return this.$t(
            "gateways.crowded_bank_application_form_title",
            "Complete Bank Application"
          );
      }
    },

    formNoticeText(status) {
      switch (status) {
        case "EnterBusinessInformation":
          return this.$t(
            "gateways.crowded_form_resume_text",
            "Your bank application is not finished yet. Please resume the hosted application form to continue the banking setup."
          );
        case "InProgressByAnotherUser":
          return this.$t(
            "gateways.crowded_form_in_progress_text",
            "Another contact in your organization is currently completing the bank application form."
          );
        case "ApplicationExists":
          return this.$t(
            "gateways.crowded_form_submitted_text",
            "Your bank application has been submitted. If documents are required, you can upload them on Crowded."
          );
        case "PendingCreation":
          return this.$t(
            "gateways.crowded_form_pending_text",
            "Your bank application is being set up. You can continue on Crowded."
          );
        default:
          return this.$t(
            "gateways.crowded_bank_application_form_text",
            "Your bank application is not finished yet. Please complete the hosted application form to continue the banking setup."
          );
      }
    },

    formNoticeButton(status) {
      switch (status) {
        case "EnterBusinessInformation":
          return this.$t("gateways.crowded_form_resume_button", "Resume Application");
        case "ApplicationExists":
          return this.$t("gateways.crowded_form_upload_button", "Upload Documents");
        case "PendingCreation":
          return this.$t("gateways.crowded_form_continue_button", "Continue on Crowded");
        default:
          return this.$t("gateways.crowded_bank_application_form_button", "Complete Application");
      }
    },

    // InProgressByAnotherUser is owned by another contact — never expose a resume link.
    formNoticeHasAction(status) {
      return status !== "InProgressByAnotherUser";
    },

    onPayoutUpdated(account) {
      if (account && account.status && account.status !== "missing") {
        this.showPayoutModal = false;
        // Refresh gateway data so the prompt disappears.
        const gatewayId = this.payoutSetupGatewayId;
        this.gatewayDataList = this.gatewayDataList.map((g) =>
          g.gatewayId === gatewayId ? { ...g, payout_ach_status: account.status } : g
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.notice-wrapper {
  margin-bottom: 40px;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
  box-shadow: 0 4px 30px 0 rgba(0, 0, 0, 0.1);
  background-color: #fef5f5;
  border: 1px solid #e4e8ec;
  border-radius: 8px;

  &:empty {
    display: none;
  }
}

.notice {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 15px;
  @media only screen and (min-width: 468px) {
    flex-direction: row;
    align-items: center;
    gap: initial;
  }
  &--loading {
    background-color: #f9f0ce;
  }

  &--verified {
    background-color: #f5fef8;
  }
  &__warning {
    margin-bottom: 15px;
    width: 63px;
    min-width: 63px;
    height: auto;
    flex-shrink: 0;
    align-self: center;
    @media only screen and (min-width: 468px) {
      margin-right: 30px;
      margin-bottom: initial;
    }
  }
  &__content {
    padding-right: 20px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: center;
  }
  ::v-deep &__title {
    font-family: $vito-bold;
    font-size: 16px;
    color: $fd;
    line-height: 1.125;
  }
  &__text {
    font-family: $vito-light;
    font-size: 14px;
    line-height: 1.125;
    color: $fd;
  }
  .entity-modal {
    margin-left: auto;
  }

  ::v-deep .bttn-bordered {
    border-color: $lr-100;
    color: #ffffff;
    background-color: $lr-100;
    font-family: $vito-bold;
    &--sm {
      min-width: 180px;
    }
  }
}
</style>
